import React, { useContext, useEffect, useState } from "react";
import style from "./index.module.scss";
import { Button, message, Modal } from "antd";
import CommonTable from "../../../../../components/CommonTable";
import { ColumnProps } from "antd/es/table";
import Tools from "./Components/Tools";
import {
    adjustRandomGroupMemberDelBatch,
    adjustRandomGroupMemberLeader,
    deleteGroupMember,
    GroupMemberListItem,
    GroupType,
    queryGroupMember,
    setGroupLeader
} from "service/group";
import MultiLineColumn from "components/MultiLineColumn";
import useRequestFn from "utils/useRequestFn";
import { ResultMessage } from "components/ResultMessage";
import { errorMessage, isTrue } from "service/service";
import useSelection from "../../../../../utils/v2/useSelection";
import { useAtomValue } from "jotai";
import { FlagAtom, groupIdAtom, GroupSettingsContext } from "../../store";
import CustomEmpty from "../../../../../components/CourseComponents/CustomEmpty";
import AutoGroupModal from "../GroupsList/Components/AutoGroupModal";
import useModalHandler from "utils/useModalHandler";
import SubmitError from "components/SubmitError";

interface Props {
    type?: GroupType;
    randomGroupKey?: string;
    canEdit?: boolean;
}
export default function MembersList({
    randomGroupKey,
    canEdit = true,
    type = GroupType.base
}: Props) {
    const addModal = useModalHandler();

    const rowSelection = useSelection();
    const groupId = useAtomValue(groupIdAtom);
    const flag = useAtomValue(FlagAtom);
    const { updateGroupList, updateNoGroupStudent } =
        useContext(GroupSettingsContext);

    const [searchText, setSearchText] = useState(""); //查询参数
    const [dataList, setDataList] = useState<GroupMemberListItem[]>([]); //成员列表

    const [getList, { data, loading }] = useRequestFn(queryGroupMember);

    useEffect(() => {
        if (groupId === -1) {
            setDataList([]);
            return;
        }
        getMemberList();
    }, [groupId, searchText]);

    //一键分组成功
    const onAddOk = () => {
        addModal.close();
        updateGroupList();
    };

    //获取成员列表
    const getMemberList = async () => {
        try {
            const data = await getList(groupId, searchText);
            setDataList(data);
        } catch (e) {
            ResultMessage.error(errorMessage(e));
        }
    };

    //设置组长
    const onChangeLeader = async (id: number, memberNo: string) => {
        try {
            switch (type) {
                case GroupType.base:
                    await setGroupLeader(id, groupId);
                    break;
                case GroupType.random:
                    await adjustRandomGroupMemberLeader(
                        memberNo,
                        groupId,
                        randomGroupKey!
                    );
                    break;
            }
            await getMemberList();
            message.success("设置成功");
        } catch (e) {
            ResultMessage.error(
                <SubmitError title="设置失败" content={errorMessage(e)} />,
                { width: 300 }
            );
        }
    };

    //移除，批量移除
    const onDelete = async (id: string) => {
        Modal.confirm({
            title: `确认删除小组成员？`,
            onOk: async () => {
                try {
                    switch (type) {
                        case GroupType.base:
                            await deleteGroupMember(id);
                            break;
                        case GroupType.random:
                            await adjustRandomGroupMemberDelBatch(id);
                            break;
                    }
                    await getMemberList();
                    await updateGroupList();
                    await updateNoGroupStudent();
                    message.success("删除成功");
                } catch (e) {
                    ResultMessage.error(
                        <SubmitError
                            title="删除失败"
                            content={errorMessage(e)}
                        />,
                        { width: 300 }
                    );
                }
            }
        });
    };

    const operation = (record: GroupMemberListItem) => {
        if (!canEdit) {
            return "";
        }
        return (
            <div style={{ textAlign: "right" }}>
                {!isTrue(record.isGroupLeader) && (
                    <Button
                        type="link"
                        onClick={() =>
                            onChangeLeader(record.id, record.memberNo)
                        }>
                        设为组长
                    </Button>
                )}

                <Button type="link" onClick={() => onDelete(record.id + "")}>
                    删除
                </Button>
            </div>
        );
    };

    const columns: ColumnProps<GroupMemberListItem>[] = [
        {
            title: "序号",
            dataIndex: "index",
            render: (
                text: string,
                record: GroupMemberListItem,
                index: number
            ) => <div>{index + 1}</div>
        },
        {
            title: "学生信息",
            dataIndex: "name",
            render: (text: string, record: GroupMemberListItem) => (
                <MultiLineColumn
                    name={
                        <div>
                            {!isTrue(record.isGroupLeader) && <div>{text}</div>}

                            {isTrue(record.isGroupLeader) && (
                                <div className={style.leader}>
                                    <span>[组长] </span>
                                    {text}
                                </div>
                            )}
                        </div>
                    }
                    no={record.memberNo}
                />
            )
        },
        {
            title: "学院",
            dataIndex: "college"
        },
        {
            title: "操作",
            width: 200,
            align: "center",
            render: (_, record: GroupMemberListItem) => operation(record)
        }
    ];

    return (
        <div className={style.root}>
            {groupId !== -1 && (
                <>
                    <div className={style.tools}>
                        <Tools
                            canEdit={canEdit}
                            randomGroupKey={randomGroupKey}
                            type={type}
                            setSearchText={setSearchText}
                            selected={rowSelection.selected}
                            onDelete={() =>
                                onDelete(rowSelection.selected.join(","))
                            }
                            onUpdateMemberList={getMemberList}
                        />
                    </div>
                    <CommonTable
                        rowKey="id"
                        loading={loading}
                        columns={columns}
                        rowSelection={{
                            selectedRowKeys: rowSelection.selected as number[],
                            onChange: rowSelection.setSelected
                        }}
                        dataSource={dataList}
                        pagination={false}
                        height="470px"
                        scroll={{ y: 500 }}
                    />
                </>
            )}
            {groupId === -1 && (
                <CustomEmpty
                    style={{ margin: "241px 0" }}
                    description={
                        flag ? (
                            "暂无数据"
                        ) : (
                            <span>
                                暂无小组，点击
                                <Button type="link" onClick={addModal.open}>
                                    一键分组
                                </Button>
                            </span>
                        )
                    }
                />
            )}

            {addModal.render(
                <AutoGroupModal
                    groupType={type}
                    randomGroupKey={randomGroupKey}
                    visible={addModal.visible}
                    onAddOk={onAddOk}
                    onCancel={addModal.close}
                />
            )}
        </div>
    );
}
